import { useState, useEffect } from 'react'
import reportingApi from '../../services/reportingApi'

/**
 * A match card. For most sports that is the result, the squads and how each
 * set went; for cricket it is the innings figures and every over as it was
 * bowled.
 */
const Scorecard = ({ fixtureId, onClose }) => {
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    reportingApi.scorecard(fixtureId)
      .then(setData)
      .catch((e) => setError(e.response?.data?.message || 'Could not load that match.'))
  }, [fixtureId])

  return (
    <div style={backdrop} onClick={onClose}>
      <div style={sheet} onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} style={closeBtn} aria-label="Close">×</button>

        {error && <p style={{ color: '#a4262c' }}>{error}</p>}
        {!data && !error && <p style={{ color: '#8b95a5' }}>Loading…</p>}

        {data && (() => {
          const m = data.match
          return (
            <>
              <div style={{ fontSize: 12, color: '#5c6b7f', fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase' }}>
                {[m.sport_label, m.city].filter(Boolean).join(' · ')}
              </div>
              <h2 style={{ margin: '6px 0 2px', fontSize: 22 }}>{m.home} vs {m.away}</h2>
              <p style={{ margin: 0, color: '#5c6b7f', fontSize: 13 }}>
                {[m.category, m.round, m.day, m.start_time, m.court].filter(Boolean).join(' · ')}
              </p>

              <div style={scoreBox}>
                <div style={{ fontSize: 30, fontWeight: 800 }}>
                  {m.summary || (m.home_score != null ? `${m.home_score} – ${m.away_score}` : 'Not played yet')}
                </div>
                {m.winner && (
                  <div style={{ color: '#1c6b3a', fontWeight: 700, marginTop: 6, fontSize: 13.5 }}>
                    {m.winner} went through
                  </div>
                )}
              </div>

              {/* Set-by-set, when the sport has sets. */}
              {Array.isArray(m.sets) && m.sets.length > 0 && (
                <>
                  <h3 style={h3}>Game by game</h3>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {m.sets.map((s, i) => (
                      <div key={i} style={setChip}>
                        <div style={{ fontSize: 10.5, color: '#8b95a5', fontWeight: 700 }}>#{i + 1}</div>
                        <div style={{ fontSize: 16, fontWeight: 800 }}>{s.home}–{s.away}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {data.cricket && <CricketCard cricket={data.cricket} />}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginTop: 22 }}>
                <Squad title={m.home} members={data.squads.home} />
                <Squad title={m.away} members={data.squads.away} />
              </div>
            </>
          )
        })()}
      </div>
    </div>
  )
}

const CricketCard = ({ cricket }) => (
  <>
    <h3 style={h3}>Innings</h3>
    {cricket.innings.map((i) => (
      <div key={i.innings} style={inningsBox}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <strong style={{ fontSize: 15 }}>{i.batting || `Innings ${i.innings}`}</strong>
          <span style={{ fontSize: 19, fontWeight: 800 }}>
            {i.runs}/{i.wickets} <span style={{ fontSize: 13, color: '#5c6b7f' }}>({i.overs})</span>
          </span>
        </div>
        <div style={{ fontSize: 12, color: '#5c6b7f', marginTop: 4 }}>
          {i.boundaries.fours} fours · {i.boundaries.sixes} sixes · {i.extras} extras
        </div>

        {i.over_by_over.length > 0 && (
          <div style={{ marginTop: 12, display: 'grid', gap: 6 }}>
            {i.over_by_over.map((o) => (
              <div key={o.over} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ width: 40, fontSize: 11.5, color: '#8b95a5', fontWeight: 700 }}>
                  Ov {o.over}
                </span>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', flex: 1 }}>
                  {o.balls.map((b, k) => <Ball key={k} b={b} />)}
                </div>
                <span style={{ fontWeight: 800, fontSize: 13 }}>{o.runs}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    ))}
  </>
)

const Ball = ({ b }) => {
  const wicket = b === 'W'
  const boundary = b === '4' || b === '6'
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      minWidth: 24, height: 24, padding: '0 5px', borderRadius: 12,
      fontSize: b.length > 2 ? 9 : 11, fontWeight: 800,
      background: wicket ? '#c2412d' : boundary ? '#1c6b3a' : '#eef1f5',
      color: (wicket || boundary) ? '#fff' : '#42505f',
    }}>{b}</span>
  )
}

const Squad = ({ title, members = [] }) => (
  <div>
    <h3 style={{ ...h3, marginTop: 0 }}>{title}</h3>
    {members.length === 0 ? (
      <p style={{ color: '#8b95a5', fontSize: 13 }}>No squad recorded.</p>
    ) : (
      <ol style={{ margin: 0, paddingLeft: 18 }}>
        {members.map((p, i) => (
          <li key={i} style={{ fontSize: 13, padding: '2px 0' }}>
            {p.name}
            <span style={{ color: '#8b95a5' }}> · {p.reg_id}</span>
            {p.is_captain ? <strong style={{ color: '#28527a' }}> (c)</strong> : null}
          </li>
        ))}
      </ol>
    )}
  </div>
)

const backdrop = { position: 'fixed', inset: 0, background: 'rgba(11,18,32,.55)', display: 'grid', placeItems: 'center', padding: 20, zIndex: 100 }
const sheet = { position: 'relative', background: '#fff', borderRadius: 14, padding: 26, width: 'min(720px, 96vw)', maxHeight: '90vh', overflowY: 'auto' }
const closeBtn = { position: 'absolute', top: 12, right: 14, border: 0, background: 'transparent', fontSize: 26, lineHeight: 1, cursor: 'pointer', color: '#8b95a5' }
const scoreBox = { marginTop: 16, padding: 18, background: '#f7f9fb', border: '1px solid #e4e9ef', borderRadius: 12, textAlign: 'center' }
const h3 = { fontSize: 14, margin: '22px 0 10px', textTransform: 'uppercase', letterSpacing: '.08em', color: '#5c6b7f' }
const setChip = { padding: '8px 14px', background: '#f7f9fb', border: '1px solid #e4e9ef', borderRadius: 10, textAlign: 'center' }
const inningsBox = { padding: 16, background: '#fbfcfd', border: '1px solid #e4e9ef', borderRadius: 12, marginBottom: 10 }

export default Scorecard
